import React from "react"
import { Table } from "@tanstack/react-table"
import { ChevronDoubleLeft, ChevronDoubleRight, ChevronLeft, ChevronRight } from "react-bootstrap-icons"
import { classNames } from "../../../../utils/classNames"
import { TableRowData } from "../types"
import { getPaginationPageNumbers, PAGINATION_GAP, PAGINATION_WINDOW_SIZE } from "../utils"

type Props<TRowData> = {
    table: Table<TRowData>
    className?: string
    scrollTargetRef?: React.RefObject<HTMLElement>
}

export const TablePagination = <TRowData extends TableRowData>(props: Props<TRowData>) => {
    const pageCount = props.table.getPageCount()

    if (pageCount <= 1) {
        return null
    }

    const currentPage = Math.min(props.table.getState().pagination.pageIndex + 1, pageCount)
    const pages = getPaginationPageNumbers(currentPage, pageCount)
    const currentPageArrayIndex = pages.indexOf(currentPage)

    const scrollToTop = () => props.scrollTargetRef?.current?.scrollIntoView({ behavior: "smooth", block: "start" })

    return (
        <div className={classNames(classes.wrap, props.className)}>
            <button
                type="button"
                aria-label="Previous page"
                className={classes.arrowButton}
                disabled={!props.table.getCanPreviousPage()}
                onClick={() => {
                    props.table.previousPage()
                    scrollToTop()
                }}>
                <ChevronLeft className={classes.icon} />
            </button>
            {pages.map((page, index) => {
                if (page !== PAGINATION_GAP) {
                    return (
                        <button
                            key={page}
                            type="button"
                            aria-label={`Page ${page}`}
                            aria-current={page === currentPage}
                            className={classNames(classes.pageButton, page === currentPage && classes.pageButtonActive)}
                            onClick={() => {
                                props.table.setPageIndex(page - 1)
                                scrollToTop()
                            }}>
                            {page}
                        </button>
                    )
                }

                const isBack = index < currentPageArrayIndex
                const targetPage = isBack
                    ? Math.max(1, currentPage - PAGINATION_WINDOW_SIZE)
                    : Math.min(pageCount, currentPage + PAGINATION_WINDOW_SIZE)

                return (
                    <button
                        key={`ellipsis-${index}`}
                        type="button"
                        aria-label={
                            isBack
                                ? `Jump back ${PAGINATION_WINDOW_SIZE} pages`
                                : `Jump forward ${PAGINATION_WINDOW_SIZE} pages`
                        }
                        className={classNames(classes.pageButton, classes.ellipsisButton, "group")}
                        onClick={() => {
                            props.table.setPageIndex(targetPage - 1)
                            scrollToTop()
                        }}>
                        <span className={classes.ellipsisDots}>…</span>
                        <span className={classes.ellipsisIcon}>
                            {isBack ? (
                                <ChevronDoubleLeft className={classes.icon} />
                            ) : (
                                <ChevronDoubleRight className={classes.icon} />
                            )}
                        </span>
                    </button>
                )
            })}
            <button
                type="button"
                aria-label="Next page"
                className={classes.arrowButton}
                disabled={!props.table.getCanNextPage()}
                onClick={() => {
                    props.table.nextPage()
                    scrollToTop()
                }}>
                <ChevronRight className={classes.icon} />
            </button>
        </div>
    )
}

const classes = {
    wrap: "flex items-center justify-center gap-2 py-4",
    pageButton:
        "min-w-9 h-9 px-2 rounded-md border border-gray-200/80 bg-white text-black/80 text-14 cursor-pointer transition-colors hover:border-primary-color/40 hover:bg-primary-color/5 hover:text-primary-color",
    pageButtonActive: "border-primary-color text-primary-color font-medium hover:bg-primary-color/5",
    arrowButton:
        "flex items-center justify-center w-9 h-9 rounded-md border border-gray-200/80 bg-white text-black/80 cursor-pointer transition-colors hover:border-primary-color/40 hover:bg-primary-color/5 hover:text-primary-color disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-gray-200/80 disabled:hover:bg-white disabled:hover:text-black/80",
    icon: "w-4 h-4",
    ellipsisButton: "relative text-black/50",
    ellipsisDots: "group-hover:opacity-0 transition-opacity",
    ellipsisIcon:
        "absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity",
}
